import * as React from 'react'
import {
  Box, Typography, Button, TextField, FormControl, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, TablePagination,
  Chip, Alert,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchOffIcon from '@mui/icons-material/SearchOff'

import {sectionCardSx, filterBarSx, dataGridWrapSx, headerCellSx, cellSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE} from '../../../../styles/foundation/colors'

import {int} from '../format'
import {subcategoryRows, ARROW} from './state/packingModel'
import EmptyState from '../components/EmptyState'
import {chipSx} from '../components/tone'
import SubcategoryDrawer from './SubcategoryDrawer'

const PER_PAGE = 8

const panelAlertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.75, '& .MuiAlert-message': {lineHeight: 1.6}}
// Menu is portalled to document.body, outside the shell's zoom — matches
// disputes-v2/FilterBar.js's own selectMenuProps so an open menu lines up
// with its now-SIZE['2xs'] closed control.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}}

/** Singular/plural spelled out, matching the module's own convention (see e.g. SopPage.js's `levelCoverage`). */
export function unlistedNotice(n) {
  return n === 1
    ? '1 sub-category is carried by SKUs but missing from the list. Add it so it can be picked in a freeze request.'
    : `${int(n)} sub-categories are carried by SKUs but missing from the list. Add them so they can be picked in a freeze request.`
}

export function frozenChipLabel(n) {
  return n === 1 ? '1 approved freeze' : `${int(n)} approved freezes`
}

/**
 * The pagination trap's signature — cat/sub pairing, the listed subcats map,
 * and which sub-category-level requests exist. Deliberately excludes every
 * OTHER field a sibling panel (or Catalogue) can mutate mid-edit — a weight
 * or packing-material change touches none of the above, so the string stays
 * identical and the memoised `rows` below is never recomputed, and the page
 * is never reset, on account of an edit that has nothing to do with this
 * table. Exported so the "stays identical" / "changes" halves of that
 * contract are each tested without mounting the panel.
 */
export function rowSignature({subcats, skus, requests}) {
  return JSON.stringify(subcats)
    + (skus || []).map((s) => s.cat + '>' + s.sub).join('|')
    + (requests || []).map((r) => r.level + ':' + r.subject + ':' + r.kind).join('|')
}

/**
 * Whether a row's `sub` or any SKU carrying its cat/sub pair contains
 * `needle` — the panel's search box matches SKU codes as well as the
 * sub-category name, so typing a SKU finds which sub-category it sits in.
 */
export function matchesSearch(row, skus, needle) {
  const q = (needle || '').trim().toLowerCase()
  if (!q) return true
  if (row.sub.toLowerCase().includes(q)) return true
  return (skus || []).some((s) => s.cat === row.cat && s.sub === row.sub && s.sku.toLowerCase().includes(q))
}

/**
 * MUI's own TableSortLabel default is ascending on a fresh column; this
 * kit's rule inverts that, so the first click on any of the four data
 * columns shows its largest (or alphabetically last) value first. A second
 * click on the SAME column still just flips direction, same as MUI's own
 * default behaviour.
 */
export function nextSort(prev, key) {
  return prev.key === key ? {key, dir: prev.dir === 'asc' ? 'desc' : 'asc'} : {key, dir: 'desc'}
}

/**
 * The sub-category taxonomy table — Packing Task 5. `state`/`actions` are
 * the narrow slice and action set `PackingPage` threads down from the one
 * module-wide store (see that file's own header); this panel calls neither
 * `useFreezeStore()` nor any other store hook itself; four independent
 * stores would each hold their own copy of the dataset and a mutation made
 * in one panel would not be visible in the others.
 */
export default function SubcategoryPanel({state, actions, focusFallbackRef}) {
  const {subcats, skus, requests} = state

  const [q, setQ] = React.useState('')
  const [catFilter, setCatFilter] = React.useState('all')
  const [page, setPage] = React.useState(0)
  const [sort, setSort] = React.useState({key: null, dir: 'desc'})
  const [drawer, setDrawer] = React.useState(null) // null | {mode: 'add'|'rename'|'remove', row}

  // The pagination trap: `rows` is memoised on the SIGNATURE above, not on
  // `subcats`/`skus`/`requests` themselves — see rowSignature's own doc
  // comment for why.
  const signature = rowSignature({subcats, skus, requests})
  const rows = React.useMemo(
    () => subcategoryRows({subcats, skus, requests}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  )

  const unlisted = rows.filter((r) => !r.listed).length
  const categoryOptions = React.useMemo(() => Array.from(new Set(rows.map((r) => r.cat))).sort(), [rows])

  const filteredRows = rows.filter((r) => (catFilter === 'all' || r.cat === catFilter) && matchesSearch(r, skus, q))
  const hasActiveFilter = !!q || catFilter !== 'all'

  // Page resets on a FILTER change only, never on the data underneath —
  // see the signature note above.
  React.useEffect(() => { setPage(0) }, [q, catFilter])

  const sortedRows = React.useMemo(() => {
    if (!sort.key) return filteredRows
    const decorated = filteredRows.map((r) => ({r, key: r[sort.key]}))
    decorated.sort((a, b) => {
      if (a.key < b.key) return sort.dir === 'asc' ? -1 : 1
      if (a.key > b.key) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return decorated.map((d) => d.r)
  }, [filteredRows, sort])

  const handleSort = (key) => setSort((prev) => nextSort(prev, key))

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PER_PAGE))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = sortedRows.slice(clampedPage * PER_PAGE, clampedPage * PER_PAGE + PER_PAGE)

  const resetFilters = () => { setQ(''); setCatFilter('all') }

  // Same focus-recovery fallback CataloguePage's own drawers use (see its
  // closeMergeDrawer/closeReviewDrawer/etc.) — reused rather than a second
  // mechanism. A row's own "Rename"/"Remove" button can vanish once the
  // save lands (a rename/removal changes which rows exist), which is
  // exactly the case MUI's own focus-restore can't recover from on its own.
  const closeDrawer = React.useCallback(() => {
    setDrawer(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleAdd = (cat, sub) => {
    actions.upsertSubcat(cat, sub)
    closeDrawer()
  }
  const handleRename = (cat, from, to) => {
    actions.renameSubcat(cat, from, to)
    closeDrawer()
  }
  const handleRemove = (cat, sub, dest) => {
    actions.removeSubcat(cat, sub, dest)
    closeDrawer()
  }

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, justifyContent: 'space-between', alignItems: {xs: 'flex-start', sm: 'center'}, gap: 1.5, mb: 1.5}}>
        <Box>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
            Sub-categories
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, maxWidth: 560}}>
            The level a weight freeze is agreed at by default. Shoes and Apparel come preloaded from the source
            document; the rest are derived from this account's own product names.
          </Typography>
        </Box>
        <Button sx={primaryBtnSx} startIcon={<AddIcon sx={{fontSize: 16}} />} onClick={() => setDrawer({mode: 'add', row: null})}>
          Add sub-category
        </Button>
      </Box>

      {unlisted > 0 && (
        <Alert severity='warning' sx={{...panelAlertSx, mb: 1.5}}>{unlistedNotice(unlisted)}</Alert>
      )}

      <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 0}}>
        <TextField
          size='small'
          placeholder='Search SKU or sub-category'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          inputProps={{'aria-label': 'Search sub-categories'}}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 240}}
        />
        <FormControl size='small' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 170}}>
          <Select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} inputProps={{'aria-label': 'Filter by category'}} MenuProps={selectMenuProps}>
            <MenuItem value='all'>All categories</MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilter && (
          <Button size='small' onClick={resetFilters} sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}>
            Clear
          </Button>
        )}
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', whiteSpace: 'nowrap'}}>
          {int(filteredRows.length)} of {int(rows.length)} sub-categories across {int(categoryOptions.length)} categories
        </Typography>
      </Box>

      {filteredRows.length === 0 ? (
        <EmptyState
          icon={SearchOffIcon}
          title='No sub-categories match'
          body='Try a different search, or clear the category filter.'
          action={<Button sx={secondaryBtnSx} onClick={resetFilters}>Clear filters</Button>}
        />
      ) : (
        <Box sx={{...dataGridWrapSx, mt: 1.5}}>
          <Table sx={{minWidth: 720}}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>
                  <TableSortLabel active={sort.key === 'cat'} direction={sort.key === 'cat' ? sort.dir : 'desc'} onClick={() => handleSort('cat')}>
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellSx}>
                  <TableSortLabel active={sort.key === 'sub'} direction={sort.key === 'sub' ? sort.dir : 'desc'} onClick={() => handleSort('sub')}>
                    Sub-category
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
                  <TableSortLabel active={sort.key === 'count'} direction={sort.key === 'count' ? sort.dir : 'desc'} onClick={() => handleSort('count')}>
                    SKUs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
                  <TableSortLabel active={sort.key === 'ships'} direction={sort.key === 'ships' ? sort.dir : 'desc'} onClick={() => handleSort('ships')}>
                    Shipments
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell sx={cellSx}>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{row.cat}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>{row.sub}</Typography>
                    <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5}}>
                      {row.frozen > 0 && <Chip size='small' label={frozenChipLabel(row.frozen)} sx={chipSx('ok')} />}
                      {row.pending > 0 && <Chip size='small' label={`${int(row.pending)} with Prozo`} sx={chipSx('warn')} />}
                      {!row.listed && <Chip size='small' label='Carried by SKUs, not on the list' sx={chipSx('neutral')} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{...cellSx, textAlign: 'right'}}>
                    {row.count === 0 ? (
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>Empty</Typography>
                    ) : (
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>{int(row.count)}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{...cellSx, textAlign: 'right'}}>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{int(row.ships)}</Typography>
                  </TableCell>
                  <TableCell sx={{...cellSx, textAlign: 'right'}}>
                    <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                      {!row.listed && (
                        <Button
                          size='small'
                          sx={secondaryBtnSx}
                          aria-label={`Add ${row.cat}${ARROW}${row.sub} to the sub-category list`}
                          onClick={() => actions.upsertSubcat(row.cat, row.sub)}
                        >
                          Add to list
                        </Button>
                      )}
                      <Button
                        size='small'
                        sx={secondaryBtnSx}
                        aria-label={`Rename ${row.cat}${ARROW}${row.sub}`}
                        onClick={() => setDrawer({mode: 'rename', row})}
                      >
                        Rename
                      </Button>
                      <Button
                        size='small'
                        sx={secondaryBtnSx}
                        aria-label={`Remove ${row.cat}${ARROW}${row.sub}`}
                        onClick={() => setDrawer({mode: 'remove', row})}
                      >
                        Remove
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component='div'
            rowsPerPageOptions={[PER_PAGE]}
            count={sortedRows.length}
            rowsPerPage={PER_PAGE}
            page={clampedPage}
            onPageChange={(e, next) => setPage(next)}
            sx={{
              '& .MuiTablePagination-toolbar': {fontFamily: FONT},
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {fontFamily: FONT, fontSize: SIZE['2xs']},
            }}
          />
        </Box>
      )}

      <SubcategoryDrawer
        open={drawer !== null}
        mode={drawer ? drawer.mode : 'add'}
        row={drawer ? drawer.row : null}
        rows={rows}
        state={state}
        onClose={closeDrawer}
        onAdd={handleAdd}
        onRename={handleRename}
        onRemove={handleRemove}
      />
    </Box>
  )
}
